// File: @/components/analytics/page-analytics.tsx

'use client';

import { c as _c } from "react/compiler-runtime";
import { useEngagementTracking } from '@/hooks/use-engagement-tracking';
import { useScrollTracking } from '@/hooks/use-scroll-tracking';

// ===================================
// TYPES
// ===================================

export interface PageAnalyticsProps {
  /** Unique identifier for the content */
  readonly contentId: string;

  /** Content category (e.g., 'landing', 'article') */
  readonly category: string;

  /** Content type for completion tracking (defaults to 'page') */
  readonly contentType?: 'page' | 'article';

  /** Whether tracking is enabled (defaults to true) */
  readonly enabled?: boolean;
}

// ===================================
// COMPONENT
// ===================================

/**
 * Drop-in analytics component for any page with scrollable content.
 *
 * Tracks:
 *   - Scroll depth milestones (25%, 50%, 75%, 90%) via useScrollTracking
 *   - Qualified visit (30s + 25% scroll) via useEngagementTracking
 *   - Content completion (60s + 75% scroll) via useEngagementTracking
 *   - Reading time when the page is hidden via useEngagementTracking
 *
 * Renders nothing — analytics only.
 *
 * @example
 * <PageAnalytics contentId="homepage" category="landing" />
 */
export function PageAnalytics(t0) {
  const $ = _c(9);
  const {
    contentId,
    category,
    contentType: t1,
    enabled: t2
  } = t0;
  const contentType = t1 === undefined ? "page" : t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== category || $[1] !== contentId || $[2] !== enabled) {
    t3 = {
      contentId,
      category,
      enabled
    };
    $[0] = category;
    $[1] = contentId;
    $[2] = enabled;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useScrollTracking(t3);
  let t4;
  if ($[4] !== category || $[5] !== contentId || $[6] !== contentType || $[7] !== enabled) {
    t4 = {
      contentId,
      category,
      contentType,
      enabled
    };
    $[4] = category;
    $[5] = contentId;
    $[6] = contentType;
    $[7] = enabled;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEngagementTracking(t4);
  return null;
}